import { Link } from "react-router-dom";
import Paper from "@mui/material/Paper";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { LuArrowUpDown } from "react-icons/lu";

import { pathToRobotDetail } from "../../../helpers/paths";
import useSort from "../../../hooks/useSort";
import { ScrollableTable } from "../../templates/ScrollableTable";
import { CleaningRobotItem } from "types/response/robot";
import { hasWarningConsumablePart } from "lib/robot";
import {
  ConsumablePart,
  getConsumablePartTypeDisplayLabel,
  getConsumablePartValueDisplayLabel,
  parseConsumablePartType,
} from "utils/robot/consumablePart";
import { parseExternalServiceCodeType } from "types/externalService";

type TableData = {
  robot: CleaningRobotItem;
  part: ConsumablePart;
};

type PartsStateTableProps = {
  robots: CleaningRobotItem[];
};

export const PartsStateTable = ({ robots }: PartsStateTableProps) => {
  const tableDatas: TableData[] = robots.flatMap((r) => {
    if (r.consumableParts.length === 0) return [];
    return r.consumableParts.map((p) => ({ robot: r, part: p }));
  });

  const {
    order,
    orderBy,
    createSortHandler,
    stableSort,
    getComparator,
    orderByGetters,
  } = useSort<TableData>("asc", [(c) => c.robot.robotName]);

  return (
    <ScrollableTable component={Paper}>
      <Table sx={{ whiteSpace: "nowrap" }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === "hasWarning"}
                direction={orderBy === "hasWarning" ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler("hasWarning", [
                  (r) => isWarningConsumablePart(r.part),
                ])}
              >
                {orderBy === "hasWarning" ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "name"}
                direction={orderBy === "name" ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler("name", [(r) => r.robot.robotName])}
              >
                ロボット
                {orderBy === "name" ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>
            <TableCell style={{ minWidth: 110 }}>
              <TableSortLabel
                active={orderBy === `consumableParts.type`}
                direction={orderBy === `consumableParts.type` ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler(`consumableParts.type`, [
                  (r) => r.part.type,
                ])}
              >
                パーツ名
                {orderBy === `consumableParts.type` ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>

            <TableCell style={{ minWidth: 110 }}>
              <TableSortLabel
                active={orderBy === `consumableParts.state`}
                direction={orderBy === `consumableParts.state` ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler(`consumableParts.state`, [
                  (r) => {
                    const exSrvCode = parseExternalServiceCodeType(
                      r.robot.exSrvCode
                    );
                    if (!exSrvCode) return "unknown";
                    const parsedPartType = parseConsumablePartType(r.part.type);
                    if (!parsedPartType) return "unknown";
                    const valueLabel = getConsumablePartValueDisplayLabel(
                      parsedPartType,
                      r.part.usage,
                      r.part.max,
                      exSrvCode
                    );
                    return valueLabel;
                  },
                ])}
              >
                使用可能時間
                {orderBy === `consumableParts.state` ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {stableSort(tableDatas, getComparator(order, orderByGetters)).map(
            (data, idx) => {
              const hasWarning = hasWarningConsumablePart(data.part);
              const exSrvCode = parseExternalServiceCodeType(
                data.robot.exSrvCode
              );
              if (!exSrvCode) {
                console.log({
                  msg: "不明な値を取得しました",
                  code: data.robot.exSrvCode,
                });
                return null;
              }
              const parsedPartType = parseConsumablePartType(data.part.type);
              if (!parsedPartType) {
                console.warn({
                  msg: "不明なパーツです",
                  type: data.part.type,
                  code: exSrvCode,
                });
              }

              const typeLabel = parsedPartType
                ? getConsumablePartTypeDisplayLabel(parsedPartType, exSrvCode)
                : "不明なパーツ";
              const valueLabel = parsedPartType
                ? getConsumablePartValueDisplayLabel(
                    parsedPartType,
                    data.part.usage,
                    data.part.max,
                    exSrvCode
                  )
                : "-";
              return (
                <TableRow key={`part-${idx}`}>
                  <TableCell>
                    {hasWarning ? (
                      <ReportProblemIcon fontSize="small" color="error" />
                    ) : (
                      <ReportProblemIcon
                        fontSize="small"
                        sx={{ color: "transparent" }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Link to={pathToRobotDetail(data.robot.robotId)}>
                      {data.robot.robotName}
                    </Link>
                  </TableCell>
                  <TableCell>{typeLabel}</TableCell>
                  <TableCell
                    sx={{
                      color: (theme) =>
                        hasWarning ? theme.palette.error.main : "inherit",
                    }}
                  >
                    {hasWarning && (
                      <ReportProblemIcon
                        fontSize="small"
                        color="error"
                        style={{
                          marginRight: "0.25rem",
                          verticalAlign: "middle",
                        }}
                      />
                    )}
                    {valueLabel}
                  </TableCell>
                </TableRow>
              );
            }
          )}
        </TableBody>
      </Table>
    </ScrollableTable>
  );
};

// ----------------------------------------------------
// ローカル
// ----------------------------------------------------
const isWarningConsumablePart = (part: ConsumablePart): boolean => {
  return part.max * 0.8 < part.usage;
};
